// Brand typography utility functions
import { ScreenType } from 'click-types';

/**
 * Typography style properties
 */
interface TypographyStyles {
  fontSize?: number | string;
  fontFamily?: string;
  fontWeight?: string | number;
  fontStyle?: 'normal' | 'italic';
  lineHeight?: number | string;
  letterSpacing?: number | string;
  textDecorationLine?:
    | 'none'
    | 'underline'
    | 'line-through'
    | 'underline line-through';
}

/**
 * Brand typography configuration
 */
interface BrandTypography {
  values?: {
    [key in ScreenType]?: TypographyStyles;
  };
}

/**
 * Component attributes with style and brand typography
 */
interface ComponentAttributes {
  id?: string;
  style?: {
    brand?: {
      typography?: BrandTypography;
    };
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Apply brand typography styles to component attributes
 * @param attrs - Component attributes containing style and brand typography
 * @param screenType - The screen type to apply (desktop, tablet, mobile)
 * @returns Modified attributes with applied typography styles
 */
export const applyBrandTypography = (
  attrs: ComponentAttributes,
  screenType: ScreenType
): ComponentAttributes => {
  const brandTypography = attrs.style?.brand?.typography;

  if (!brandTypography || !brandTypography.values) {
    return attrs;
  }

  const appliedTypographyStyles = brandTypography.values[screenType];

  if (!appliedTypographyStyles) {
    return attrs;
  }

  const modifiedAttrs = {
    ...attrs,
    ...attrs.style,
    ...{
      style: {
        ...attrs.style,
        ...appliedTypographyStyles,
      },
    },
  };

  return modifiedAttrs;
};

/**
 * Get typography styles for a specific screen type from brand typography
 * @param brandTypography - Brand typography configuration
 * @param screenType - Target screen type
 * @returns Typography styles for the screen type or null if not found
 */
export const getTypographyStylesForScreen = (
  brandTypography: BrandTypography | undefined,
  screenType: ScreenType
): TypographyStyles | null => {
  if (!brandTypography?.values?.[screenType]) {
    return null;
  }

  return brandTypography.values[screenType]!;
};

/**
 * Check if component has brand typography configured
 * @param attrs - Component attributes
 * @returns True if brand typography is configured
 */
export const hasBrandTypography = (attrs: ComponentAttributes): boolean => {
  return !!(
    attrs.style?.brand?.typography?.values &&
    Object.keys(attrs.style.brand.typography.values).length > 0
  );
};
